import {useLoadingStore} from "@/stores/loading";
import {useDrmPfInformationSearch} from "@/stores/drmPfInformationSearch";
import {useEffect, useRef, useState} from "react";
import {ConditionList, DrmPfInformation} from "@/types/drmPfInformation";
import * as Common from "@/utils/common";
import {axios} from '@/lib/axios'
import {getDrmPfInformationList, getSearchConditionList} from '@/api/drmPfInformation'

export default function useDrmPfInformationSearchHooks() {
  const setLoading = useLoadingStore(state => state.setLoading)
  const {
    order,
    setOrder,
    pageSize,
    setPageSize,
    checkTypeIds,
    setCheckTypeIds,
    checkKindIds,
    setCheckKindIds,
    searchText,
    setSearchText,
    searchYear,
    setSearchYear,
    currentPage,
    setCurrentPage
  } = useDrmPfInformationSearch()

  const [isShowSearchOptions, setIsShowSearchOptions] = useState(false)
  const [isShowKeywordList, setIsShowKeywordList] = useState(false)

  const [lastModified, setLastModified] = useState("")
  const [drmPfInformationList, setDrmPfInformationList] = useState<DrmPfInformation[]>([])
  const [conditionList, setConditionList] = useState<ConditionList>()

  const [displayDrmPfInformationList, setDisplayDrmPfInformationList] = useState<DrmPfInformation[]>([])
  const [currentDrmPfInformationList, setCurrentDrmPfInformationList] = useState<DrmPfInformation[]>([])
  const [firstItemNum, setFirstItemNum] = useState<number>(0)
  const [lastItemNum, setLastItemNum] = useState<number>(0)

  const onClickSearchScrollRef = useRef<HTMLDivElement>(null);

  const onClickSearchOptions = () => setIsShowSearchOptions(!isShowSearchOptions);
  const onClickKeywordList = () => setIsShowKeywordList(!isShowKeywordList);

  const onClickSearch = () => {
    const filterDrmPfInformations = Common.filterDrmPfInformations(drmPfInformationList, checkTypeIds, searchText || "", searchYear);
    setDisplayDrmPfInformationList(filterDrmPfInformations);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, pageSize, filterDrmPfInformations);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentDrmPfInformationList(currentItems);
    setCurrentPage(0);
    Common.smoothScroll(onClickSearchScrollRef, -70);
  }
  const onClickReset = () => {
    setCheckTypeIds([])
    setCheckKindIds([])
    if (searchText) {
      setSearchText("")
    }
  }

  //検索オプション＿情報区分
  const onClickCheckType = (id: number) => {
    const newIds = checkTypeIds.includes(id) ? checkTypeIds.filter(typeId => typeId !== id) : [...checkTypeIds, id]
    setCheckTypeIds(newIds);
  }
  //検索オプション＿情報種別
  const onClickCheckKind = (id: number) => {
    const newIds = checkKindIds.includes(id) ? checkKindIds.filter(kindId => kindId !== id) : [...checkKindIds, id]
    setCheckKindIds(newIds);
  }
  //検索＿キーワード追加
  const onClickKeyword = (word: string) => {
    const updateText = searchText.length > 0 ? searchText + "　" + word : word;
    setSearchText(updateText)
  }

  //ページネーション＿表示件数変更
  const onChangePageSize = (e: any) => {
    setPageSize(e.target.value)
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, e.target.value, displayDrmPfInformationList)
    setFirstItemNum(firstItemNum)
    setLastItemNum(lastItemNum)
    setCurrentDrmPfInformationList(currentItems)
    setCurrentPage(0)
  }
  //ページネーション＿ページ移動
  const onChangePage = (e: any) => {
    setCurrentPage(e.selected)
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(e.selected, pageSize, displayDrmPfInformationList)
    setFirstItemNum(firstItemNum)
    setLastItemNum(lastItemNum)
    setCurrentDrmPfInformationList(currentItems)
  }

  // ページネーション＿先頭ページへ
  const goToFirstPage = () => {
    setCurrentPage(0);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(0, pageSize, displayDrmPfInformationList);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentDrmPfInformationList(currentItems);
  };

// ページネーション＿最後ページへ
  const goToLastPage = () => {
    const lastPage = Math.ceil(displayDrmPfInformationList.length / Number(pageSize)) - 1;
    setCurrentPage(lastPage);
    const {
      firstItemNum,
      lastItemNum,
      currentItems
    } = Common.paginateCurrentValues(lastPage, pageSize, displayDrmPfInformationList);
    setFirstItemNum(firstItemNum);
    setLastItemNum(lastItemNum);
    setCurrentDrmPfInformationList(currentItems);
  };

  //検索オプション・キーワード取得
  useEffect(() => {
    getSearchConditionList().then(result => {
      setConditionList(result.data)
    })
  }, [])

  //FAQリストの取得
  useEffect(() => {
    setLoading(true)
    getDrmPfInformationList(order).then(result => {
      const drmPfInformationData = Common.addDisplayNum(result.data.drm_pf_information_list)
      setDrmPfInformationList(drmPfInformationData)
      setLastModified(result.data.last_modified)

      const filterDrmPfInformations = Common.filterDrmPfInformations(drmPfInformationData, checkTypeIds, searchText, searchYear)
      setDisplayDrmPfInformationList(filterDrmPfInformations)
      //ページネーション
      const {
        firstItemNum,
        lastItemNum,
        currentItems
      } = Common.paginateCurrentValues(currentPage, pageSize, filterDrmPfInformations)
      setFirstItemNum(firstItemNum)
      setLastItemNum(lastItemNum)
      setCurrentDrmPfInformationList(currentItems)
    }).catch(error => {
      console.log(error)
    }).finally(() => {
      setLoading(false)
    })
  }, [order])

  return {
    lastModified,
    onClickSearch,
    isShowSearchOptions,
    onClickSearchOptions,
    isShowKeywordList,
    onClickKeywordList,
    conditionList,
    onClickCheckType,
    onClickCheckKind,
    checkTypeIds,
    checkKindIds,
    onClickKeyword,
    onClickReset,
    displayDrmPfInformationList,
    firstItemNum,
    lastItemNum,
    pageSize,
    onChangePageSize,
    order,
    setOrder,
    goToFirstPage,
    onChangePage,
    currentPage,
    goToLastPage,
    currentDrmPfInformationList,
    onClickSearchScrollRef
  };
}